import { useEffect, useRef, useState } from "react";
import { useFetcher } from "react-router";
import { Icon } from "#app/components/ui/icon.tsx";
import { getAudioSrc } from "#app/utils/misc.tsx";

interface AudioEmbedProps {
	word: string;
}

export function AudioEmbed({ word }: AudioEmbedProps) {
	const fetcher = useFetcher<{
		audio?: Array<{ objectKey: string; isPrimary: boolean }>;
	}>();
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);

	useEffect(() => {
		if (fetcher.state === "idle" && !fetcher.data) {
			void fetcher.load(`/resources/audio-for-word/${word}`);
		}
	}, [fetcher, word]);

	const audio = fetcher.data?.audio;
	const primaryAudio = audio?.find((a) => a.isPrimary) ?? audio?.[0];

	if (fetcher.state === "loading") {
		return (
			<div className="my-4 flex items-center gap-2 text-body-xs text-muted-foreground">
				Loading pronunciation...
			</div>
		);
	}

	if (!primaryAudio) {
		return null;
	}

	function handlePlay() {
		const el = audioRef.current;
		if (!el) return;
		if (isPlaying) {
			el.pause();
			el.currentTime = 0;
			setIsPlaying(false);
		} else {
			void el.play();
			setIsPlaying(true);
		}
	}

	return (
		<div className="my-4 flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3">
			<button
				type="button"
				onClick={handlePlay}
				className="flex size-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
				aria-label={isPlaying ? "Stop pronunciation" : "Play pronunciation"}
			>
				<Icon name={isPlaying ? "square" : "play"} className="size-4" />
			</button>
			<div className="flex flex-col">
				<span className="text-body-sm font-medium text-foreground">
					Listen to pronunciation
				</span>
				<span className="text-body-2xs text-muted-foreground">
					Community recording
				</span>
			</div>
			<audio
				ref={audioRef}
				src={getAudioSrc(primaryAudio.objectKey)}
				preload="none"
				onEnded={() => setIsPlaying(false)}
			/>
		</div>
	);
}
